import type { AiIdentification, AiIdentificationNewRecordParameter } from "@/services/aiIdentification/aiIdentificationTypes";
import { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { CollectionItem } from "@/services/collection/item/collectionItemTypes";
import { createCollectionItemFromCollectible } from "@/services/collection/item/collectionItemFromCollectible";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item/collectionItemApi";
import { orderFrontBackGalleryImages, setImagesFromAiIdentification, uploadFrontBackPhotos, type FrontBackPhotos } from "@/services/collection/item/gallery/api";
import { batchSetCollectionItemParameters, type ParameterSetRequest } from "@/services/parameters/parametersApi";
import PostHog from "@/services/analytics/postHog";
import { factoryImage, type Image } from "@/services/images";
import type { Param } from "@/services/collectibles/elastic/collectiblesElasticHelpers";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { ItemParameter } from "@/services/parameters/parametersSchema";
import type { PriceNumeric } from "@/services/prices/priceTypes";
import { isNumericPrice } from "@/services/prices/priceValidators";
import { withRetry } from "@/utilities/retry";

// {valueId} entries resolve to a real catalog value; {customValue} ones are the AI's own free-text
// reading. An entry with neither (both null) carries nothing worth setting.
const newRecordParametersToRequests = (parameters: AiIdentificationNewRecordParameter[]): ParameterSetRequest[] =>
  parameters.flatMap((p): ParameterSetRequest[] => {
    if (p.valueId !== null) return [{ keyId: p.keyId, valueId: p.valueId }];
    if (p.customValue !== null) return [{ keyId: p.keyId, customValue: p.customValue }];
    return [];
  });

// No confident catalog link (Enriched, or "žádný z nich" on Candidates) - mint a plain collection
// item named/parametrized from what the AI itself read off the photo, mirroring the manual add-item
// flow (Strategy.Manual) rather than createCollectionItemFromCollectible's catalog-linked path.
const createCollectionItemFromNewRecordParameters = async (identification: AiIdentification, photos?: FrontBackPhotos, primaryParams: ParameterSetRequest[] = []): Promise<CollectionItem> => {
  const item = await withRetry(() =>
    createCollectionItem({
      categoryId: identification.categoryId,
      customName: identification.name,
      userCollectionListName: "",
    })
  );

  const tasks: Promise<unknown>[] = [withRetry(() => updateCollectionItem(item.id, { aiIdentificationSourceId: identification.id })), withRetry(() => updateCollectionItem(item.id, { publicNote: identification.description ?? "" }))];
  let photoIds: Awaited<ReturnType<typeof uploadFrontBackPhotos>> = {};

  const paramRequests = [...primaryParams, ...newRecordParametersToRequests(identification.newRecordParameters)];
  if (paramRequests.length > 0) {
    tasks.push(withRetry(() => batchSetCollectionItemParameters(item.id, paramRequests)));
  }

  if (photos) {
    tasks.push(
      uploadFrontBackPhotos(item, photos).then((ids) => {
        photoIds = ids;
      })
    );
  }

  await Promise.all(tasks);
  await orderFrontBackGalleryImages(photoIds);
  return item;
};

export const createCollectionItemFromIdentification = async (identification: AiIdentification, options?: { photos?: FrontBackPhotos; collectibleId?: CollectibleId; primaryParams?: ParameterSetRequest[] }): Promise<CollectionItem> => {
  // A picked Candidate's id never lives on `identification.collectible` (that stays null for the
  // Candidates matchType even after a local pick) - callers pass it explicitly instead. The
  // category is always the identification's own - a candidate belongs to the same category.
  const collectibleId = options?.collectibleId ?? identification.collectible?.id;

  const item =
    collectibleId !== undefined
      ? await createCollectionItemFromCollectible({
          collectibleId,
          categoryId: identification.categoryId,
          photos: options?.photos,
          aiIdentificationSourceId: identification.id,
          publicNote: identification.description ?? "",
          primaryParams: options?.primaryParams,
        })
      : await createCollectionItemFromNewRecordParameters(identification, options?.photos, options?.primaryParams);

  if (!options?.photos) {
    await setImagesFromAiIdentification(item, identification);
  }

  PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
  return item;
};

export const SupportedCategoryIds = [CategoryId.SportsCards, CategoryId.Pokemon];

export const getImages = (item: AiIdentification): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const hasPrice = (item: AiIdentification): item is AiIdentification & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);

// Resolves the AI's raw keyId/valueId/customValue parameters (no confident catalog link) into
// displayable name/value pairs, using the category's own parameter definitions for labels.
export const getNewRecordParams = (newRecordParameters: AiIdentificationNewRecordParameter[], categoryParameters: ItemParameter[]): Param[] => {
  const definitionsById = new Map(categoryParameters.map((p) => [p.id, p]));

  return newRecordParameters
    .flatMap((entry) => {
      const definition = definitionsById.get(entry.keyId);
      if (!definition) return []; // stale/unknown key — skip rather than show a garbage label

      const value = entry.valueId !== null ? definition.values.find((v) => v.id === entry.valueId)?.value : (entry.customValue ?? undefined);
      return [{ name: definition.name, value, order: definition.displayOrder }];
    })
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...param }) => param);
};
